import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { memberService, notificationService } from './clients/ApiClients';
import {
  MemberReturn,
  MemberUpdate,
  MemberMessageRequest,
} from './clients/rust-api-client';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import { formatPhoneNumber } from './utils/phoneUtils';
import { getUserTypeFromToken } from './utils/TokenUtils';
import {
  canResendInvitation,
  canImpersonateMember,
  canDeactivateMember,
} from './constants/UserTypes';
import { impersonationUrl } from './utils/memberPortalConfig';
import { apiErrorMessage } from './utils/apiErrors';
import { ErrorPage } from './common/Custom/ErrorPage';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from './common/Catalyst/dropdown';
import {
  EllipsisHorizontalIcon,
  EnvelopeIcon,
  EyeIcon,
} from '@heroicons/react/16/solid';
import { memberInitials, memberStatus, memberStatusLabel } from './member/memberMeta';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { Navbar } from './common/Catalyst/navbar';
import AppSidebar from './AppSidebar';
import ViolationList from './ViolationList';
import WorkOrderList from './WorkOrderList';
import { CreateActions } from './shell/CreateActions';
import { headerButtonClass } from './shell/CreateButton';
import HomeListComponent from './home/HomeListComponent';
import EditMemberDialog from './member/EditMemberDialog';
import MemberBankAccountCard from './member/MemberBankAccountCard';
import SendMessageDialog from './member/SendMessageDialog';
import { useSenderName } from './hooks/UserContext';
import { fresher, useSyncedMember } from './sync/hooks';

interface AlertState {
  type: 'success' | 'error';
  title: string;
  message: string;
  show: boolean;
}

const MemberDetailPage: React.FC = () => {
  const { hoaId, memberId } = useParams();
  const [fetchedMember, setMember] = useState<MemberReturn | null>(null);
  // The sync engine's local copy when it has this member (#561) — it may
  // not: a Manager's copy holds members only through homes they can see,
  // so a member reached some other way is fetched.
  const syncedMember = useSyncedMember(memberId);
  const member = fresher(syncedMember, fetchedMember);
  const haveSynced = !!syncedMember;
  const [loading, setLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [resending, setResending] = useState(false);
  const [opening, setOpening] = useState(false);
  const [changingAccess, setChangingAccess] = useState(false);
  // Why the member could not be loaded, and a counter "Try again" bumps
  // to run the load again.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [alert, setAlert] = useState<AlertState>({
    type: 'success',
    title: '',
    message: '',
    show: false,
  });

  const senderName = useSenderName();
  const userType = getUserTypeFromToken();

  const showAlert = (
    type: 'success' | 'error',
    title: string,
    message: string
  ) => {
    setAlert({
      type,
      title,
      message,
      show: true,
    });
  };

  useEffect(() => {
    const fetchMember = async () => {
      if (!memberId) return;
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setLoadError(null);
        const response = await memberService.getMember({
          id: memberId,
        });
        setMember(response.data);
      } catch (err) {
        console.error('Error fetching member:', err);
        setLoadError(apiErrorMessage(err, "Couldn't load this member."));
      } finally {
        setLoading(false);
      }
    };

    fetchMember();
  }, [memberId, haveSynced, reloadKey]);

  const handleUpdateMember = async (updateData: MemberUpdate) => {
    try {
      setLoading(true);
      await memberService.updateMember({
        id: memberId!,
        memberUpdate: updateData,
      });
      // Refresh member data
      const response = await memberService.getMember({
        id: memberId!,
      });
      setMember(response.data);
      showAlert('success', 'Success', 'Member updated successfully!');
    } catch (err) {
      console.error('Error updating member:', err);
      showAlert(
        'error',
        "Couldn't update member",
        apiErrorMessage(err, "Couldn't save the member.")
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Open a read-only view of this homeowner's portal.
   *
   * **The token travels in the URL fragment**, which browsers never send
   * to a server — no access log, no proxy trace, no `Referer`. The
   * portal's landing page strips it from the address bar on arrival.
   *
   * A new tab rather than this one, so the manager keeps their own
   * session: the two apps are different origins and neither disturbs the
   * other, but losing your place mid-support-call is its own annoyance.
   *
   * Everything that decides whether this is allowed happens server-side —
   * access to every HOA the homeowner has a home in, and for a board
   * member the association's own switch. A 403 here is that decision, not
   * a bug.
   */
  const handleImpersonate = async () => {
    if (!memberId) return;
    setOpening(true);
    try {
      const response = await memberService.impersonateMember({
        id: memberId,
      });
      window.open(
        impersonationUrl(response.data.access_token),
        '_blank',
        'noopener,noreferrer'
      );
    } catch (err) {
      console.error('Error starting impersonation:', err);
      showAlert(
        'error',
        'Cannot Open This View',
        apiErrorMessage(err, "You cannot view this homeowner's account.")
      );
    } finally {
      setOpening(false);
    }
  };

  /**
   * Resend the member's WorkOS invitation.
   *
   * Since sign-in moved to AuthKit this is the **only** repair for a
   * homeowner who cannot get in: there is no password to reset and no
   * setup link to reissue. It covers two states — an invitation already
   * issued (WorkOS resends the same one; it refuses to create a second
   * for a pending address) and a member never invited at all, which is
   * what a WorkOS outage during creation leaves behind.
   *
   * The backend distinguishes four outcomes and writes a sentence for
   * each. They are surfaced verbatim rather than flattened to "sent",
   * because a manager acts differently on each: "already pending" means
   * wait, "already has a sign-in" means the member can sign in now, and
   * "could not send" means try again. `email_sent` is what separates the
   * last one — it answers 200 either way, since the member row is fine
   * and only the delivery failed.
   */
  const handleResendInvitation = async () => {
    if (!memberId) return;
    setResending(true);
    try {
      const response = await memberService.resendSetupEmail({
        id: memberId,
      });
      showAlert(
        response.data.email_sent ? 'success' : 'error',
        response.data.email_sent ? 'Invitation Sent' : 'Not Sent',
        response.data.message
      );

      // "Already has a sign-in" activates the member server-side, so the
      // status on screen would otherwise contradict the message next to
      // it.
      const refreshed = await memberService.getMember({ id: memberId });
      setMember(refreshed.data);
    } catch (err) {
      console.error('Error resending invitation:', err);
      // The backend's own words where it has them — "This member has
      // already set up their account." is actionable; a generic failure
      // is not.
      showAlert(
        'error',
        'Could Not Resend',
        apiErrorMessage(
          err,
          "Couldn't resend the invitation. Please try again."
        )
      );
    } finally {
      setResending(false);
    }
  };

  /**
   * Switch the member's sign-in off, or back on (#171).
   *
   * Deactivating ends their sessions at once and keeps them out until a
   * manager reactivates them; accepting their old invitation again does
   * not undo it. Homes, ledger and history are untouched — this is about
   * access only, so it is no substitute for removing a former owner from a
   * home.
   */
  const handleChangeAccess = async (deactivate: boolean) => {
    if (!memberId || !member) return;
    if (
      deactivate &&
      !window.confirm(
        `Deactivate ${member.display_name}? They are signed out now and cannot sign in to the member portal until reactivated.`
      )
    ) {
      return;
    }
    setChangingAccess(true);
    try {
      const response = deactivate
        ? await memberService.deactivateMember({ id: memberId })
        : await memberService.reactivateMember({ id: memberId });
      setMember(response.data);
      showAlert(
        'success',
        deactivate ? 'Member Deactivated' : 'Member Reactivated',
        deactivate
          ? `${member.display_name} can no longer sign in.`
          : `${member.display_name} can sign in again.`
      );
    } catch (err) {
      console.error('Error changing member access:', err);
      showAlert(
        'error',
        deactivate ? "Couldn't Deactivate" : "Couldn't Reactivate",
        apiErrorMessage(err, 'Please try again.')
      );
    } finally {
      setChangingAccess(false);
    }
  };

  // A failure is left to propagate: the dialog stays open and says why.
  const handleSendMessage = async (messageData: MemberMessageRequest) => {
    await notificationService.sendMemberMessage({
      memberMessageRequest: messageData,
    });
    showAlert(
      'success',
      'Message Sent',
      'Your message has been sent successfully.'
    );
  };

  // With the row in the sync store there is nothing to wait for: render
  // it on the first paint, not after the effect clears `loading` (#635).
  if (loading && !haveSynced) {
    return <LoadingSpinner />;
  }

  if (!member) {
    return (
      <SidebarLayout
        sidebar={<AppSidebar currentPage="member_detail" />}
        navbar={<Navbar />}
      >
        <ErrorPage
          title={loadError ? "Couldn't load this member" : 'Member not found'}
          message={
            loadError ??
            'It may have been deleted, or you may not have access to it.'
          }
          onRetry={loadError ? () => setReloadKey((k) => k + 1) : undefined}
        />
      </SidebarLayout>
    );
  }

  const fullName = [member.first_name, member.middle_name, member.last_name]
    .filter(Boolean)
    .join(' ');
  const status = memberStatus(member);
  const fact = 'flex w-full items-baseline justify-between gap-3 px-2 py-1 text-[13px]';
  const sectionTitle = 'mb-1.5 px-2 text-[12px] font-medium text-fg-muted';

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="member_detail" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      {/* Header strip (#603 follow-on 3). */}
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link
          to={`/hoa/${hoaId}?tab=members`}
          className="text-fg-muted hover:text-fg"
        >
          Members
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        <span className="min-w-0 truncate font-medium text-fg">{fullName}</span>
        <div className="ml-auto flex items-center gap-2">
          {/* Creates for this member: the pickers offer only their own
              associations and homes. */}
          <CreateActions scope={{ memberId: member.id }} violation workOrder />
          {/* Only while the member has not set up their account: once
              they have, the backend refuses with a 400 and the button
              would be an invitation to hit it. Not for a deactivated
              member either (409): reactivating is their way back. It
              sends email, so it stays a labelled button rather than a
              menu item. */}
          {status === 'invited' && canResendInvitation(userType) && (
            <button
              type="button"
              onClick={handleResendInvitation}
              disabled={resending}
              className={headerButtonClass}
            >
              <EnvelopeIcon className="h-3.5 w-3.5" aria-hidden />
              {resending ? 'Sending…' : 'Resend invitation'}
            </button>
          )}
          {/* From main (#593). A labelled button, not a menu item: it opens
              another app as this homeowner, and the server records it.
              Only for an active member -- there is no portal to see before
              they have set up their account. */}
          {member.is_active && canImpersonateMember(userType) && (
            <button
              type="button"
              onClick={handleImpersonate}
              disabled={opening}
              title="See the member portal exactly as this homeowner sees it. Read-only, and recorded."
              className={headerButtonClass}
            >
              <EyeIcon className="h-3.5 w-3.5" aria-hidden />
              {opening ? 'Opening…' : 'View as homeowner'}
            </button>
          )}
          <Dropdown>
            <DropdownButton plain aria-label="More actions">
              <EllipsisHorizontalIcon className="h-4 w-4" />
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              <DropdownItem onClick={() => setIsMessageDialogOpen(true)}>Send message…</DropdownItem>
              <DropdownItem onClick={() => setIsEditDialogOpen(true)}>Edit details…</DropdownItem>
              {/* Only a member who has set up their account can be
                  deactivated; the backend refuses anyone else (409). */}
              {status === 'active' && canDeactivateMember(userType) && (
                <DropdownItem onClick={() => handleChangeAccess(true)} disabled={changingAccess}>
                  Deactivate…
                </DropdownItem>
              )}
              {status === 'deactivated' && canDeactivateMember(userType) && (
                <DropdownItem onClick={() => handleChangeAccess(false)} disabled={changingAccess}>
                  Reactivate
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>

      <ShowAlert
        alertType={alert.type}
        alertTitle={alert.title}
        alertMessage={alert.message}
        isVisible={alert.show}
        onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
        timeout={5000}
        isInline={true}
      />

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div className="min-w-0 space-y-10">
          <div className="flex items-center gap-4">
            <span
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-fg/10 text-base font-semibold text-fg-secondary"
              aria-hidden
            >
              {memberInitials(member)}
            </span>
            <div className="min-w-0">
              <h1 className="truncate text-2xl/8 font-semibold tracking-tight text-fg">
                {fullName}
              </h1>
              {member.preferred_name && (
                <p className="text-[15px]/6 text-fg-muted">
                  Goes by {member.preferred_name}
                </p>
              )}
            </div>
          </div>
          <HomeListComponent member={member} />
          <ViolationList memberId={member.id} title="Violations" />
          <WorkOrderList memberId={member.id} title="Work orders" />
        </div>

        {/* On narrow screens the panel drops below the main column. */}
        <aside
          aria-label="Member properties"
          className="divide-y divide-line/60 lg:sticky lg:top-6 lg:self-start"
        >
          <section className="pb-3">
            <h2 className={sectionTitle}>Properties</h2>
            <div className={fact}>
              <span className="text-fg-muted">Status</span>
              <span className="inline-flex items-center gap-1.5 text-fg">
                <span
                  className={`h-2 w-2 rounded-full ${
                    status === 'active' ? 'bg-success' : status === 'deactivated' ? 'bg-danger' : 'bg-fg-faint'
                  }`}
                  aria-hidden
                />
                {memberStatusLabel(member)}
              </span>
            </div>
            {member.deactivated_at && (
              <div className={fact}>
                <span className="text-fg-muted">Deactivated</span>
                <span className="text-fg">
                  {new Date(member.deactivated_at * 1000).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            )}
            {member.is_primary && (
              <div className={fact}>
                <span className="text-fg-muted">Role</span>
                <span className="text-fg">Primary</span>
              </div>
            )}
            <div className={fact}>
              <span className="text-fg-muted">Member since</span>
              <span className="text-fg">
                {new Date(member.created_at * 1000).toLocaleDateString(
                  'en-US',
                  {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  }
                )}
              </span>
            </div>
          </section>
          <section className="pt-3">
            <h2 className={sectionTitle}>Contact</h2>
            <div className={fact}>
              <span className="text-fg-muted">Email</span>
              <a
                href={`mailto:${member.email}`}
                className="truncate text-accent-fg hover:underline"
              >
                {member.email}
              </a>
            </div>
            <div className={fact}>
              <span className="text-fg-muted">Phone</span>
              <span className="text-fg">
                {member.phone ? formatPhoneNumber(member.phone) : '—'}
              </span>
            </div>
          </section>
          {/* A homeowner's bank account for ACH dues, masked; staff can
              remove it, only the homeowner can add one. Keyed to the HOA
              of the member's first home. */}
          <MemberBankAccountCard memberId={member.id} />
        </aside>
      </div>

      <EditMemberDialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onSave={handleUpdateMember}
        member={member}
      />

      <SendMessageDialog
        isOpen={isMessageDialogOpen}
        onClose={() => setIsMessageDialogOpen(false)}
        onSend={handleSendMessage}
        member={member}
        hoaId={hoaId!}
        senderName={senderName}
      />
    </SidebarLayout>
  );
};

export default MemberDetailPage;
